import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BackendService } from './general/backend.service';
import {
  StatementGenerationRequest,
  StatementGenerationResponse,
  StatementProcess,
  GeneratedStatement,
  SurveyInputData,
  PDFInputData,
  PubMedInputData,
  SurveyListItem,
  SurveyResults,
  GradeRevisionListItem,
  GradeDocument,
  ProcessStatusResponse
} from '../models/statement.models';
import { Observable, interval, Subject } from 'rxjs';
import { takeWhile, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class StatementGeneratorService {

  constructor(
    private backend: BackendService,
    private http: HttpClient
  ) { }

  /**
   * Generate statements from multiple input sources
   */
  generateStatements(
    request: StatementGenerationRequest,
    successCallback: (response: StatementGenerationResponse) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('statements/generate', request).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get status of statement generation process
   */
  getProcessStatus(
    processId: string,
    successCallback: (process: StatementProcess) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`statements/process/${processId}/status`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get generated statements from a completed process
   */
  getGeneratedStatements(
    processId: string,
    successCallback: (statements: GeneratedStatement[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`statements/process/${processId}/statements`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Poll process status until completion
   */
  pollProcessStatus(
    processId: string,
    pollingInterval: number = 3000,
    maxAttempts: number = 60
  ): Observable<StatementProcess> {
    let attempts = 0;
    let isComplete = false;
    const statusSubject = new Subject<StatementProcess>();

    const polling = interval(pollingInterval).pipe(
      takeWhile(() => attempts < maxAttempts && !isComplete),
      switchMap(() => {
        attempts++;
        return new Observable<StatementProcess>(observer => {
          this.getProcessStatus(
            processId,
            (process) => {
              observer.next(process);
              if (process.status === 'completed' || process.status === 'failed') {
                isComplete = true;
                observer.complete();
              }
            },
            (error) => observer.error(error)
          );
        });
      })
    ).subscribe({
      next: (process) => {
        statusSubject.next(process);
        if (process.status === 'completed' || process.status === 'failed') {
          statusSubject.complete();
        }
      },
      error: (error) => {
        statusSubject.error(error);
        statusSubject.complete();
      },
      complete: () => statusSubject.complete()
    });

    return statusSubject.asObservable();
  }

  /**
   * Get surveys for a project (completed/closed only)
   */
  getProjectSurveys(
    projectId: number,
    successCallback: (surveys: SurveyListItem[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/surveys`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get survey responses and results (detailed view)
   */
  getSurveyResults(
    surveyId: number,
    successCallback: (data: SurveyResults) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`surveys/${surveyId}/results`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get GRADE revisions for a project (completed only)
   */
  getProjectGradeRevisions(
    projectId: number,
    successCallback: (revisions: GradeRevisionListItem[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/grade-revisions`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get GRADE revision documents (detailed view)
   */
  getGradeRevisionDocuments(
    revisionId: number,
    successCallback: (documents: GradeDocument[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`grade-revisions/${revisionId}/documents`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Upload PDF document for statement generation
   */
  uploadPDFDocument(
    file: File,
    projectId: number,
    successCallback: (data: PDFInputData) => void,
    errorCallback: (error: any) => void
  ): void {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId.toString());

    // Use HttpClient directly for FormData upload
    this.http.post(`${environment.apiUrl}statements/upload-pdf`, formData, {
      withCredentials: true
    }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Fetch PubMed article by PMID
   */
  fetchPubMedArticle(
    pmid: string,
    successCallback: (data: PubMedInputData) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('tools/pubmed/fetch', { pmid }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Search PubMed articles
   */
  searchPubMed(
    query: string,
    page: number = 1,
    successCallback: (results: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post('tools/pubmed/search', { queryString: query, currentPage: page }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get literature review documents
   */
  getLiteratureReviewDocuments(
    gradeRevisionId: number,
    successCallback: (documents: any[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`grade-revisions/${gradeRevisionId}/documents`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get stored statements for a project
   */
  getProjectStatements(
    projectId: number,
    successCallback: (statements: GeneratedStatement[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/statements`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Save generated statements to project
   */
  saveStatementsToProject(
    projectId: number,
    statements: GeneratedStatement[],
    successCallback: (result: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post(`projects/${projectId}/statements`, { statements }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Update a statement
   */
  updateStatement(
    statementId: number,
    updates: Partial<GeneratedStatement>,
    successCallback: (statement: GeneratedStatement) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.put(`statements/${statementId}`, updates).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Update a statement with AI assistance
   */
  updateStatementWithAI(
    statementId: number,
    updatePrompt: string,
    successCallback: (statement: GeneratedStatement) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post(`statements/${statementId}/update-with-ai`, { prompt: updatePrompt }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Delete a statement
   */
  deleteStatement(
    statementId: number,
    successCallback: (result: any) => void,
    errorCallback: (error: any) => void
  ): void {
    // Use HttpClient directly for proper DELETE method
    // Backend expects: DELETE /api/statements/{id}
    this.http.delete(`${environment.apiUrl}statements/${statementId}`, {
      withCredentials: true
    }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Delete an entire statement generation (process and all its statements)
   */
  deleteStatementGeneration(
    processId: string,
    successCallback: (result: any) => void,
    errorCallback: (error: any) => void
  ): void {
    // Use HttpClient directly for proper DELETE method
    // Backend expects: DELETE /api/statements/process/{processId}
    this.http.delete(`${environment.apiUrl}statements/process/${processId}`, {
      withCredentials: true
    }).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get all statement generations for a project (history/list)
   */
  getProjectGenerations(
    projectId: number,
    successCallback: (generations: any[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/statement-generations`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get the latest (most recent) generation for a project
   */
  getLatestGeneration(
    projectId: number,
    successCallback: (generation: any) => void,
    errorCallback: (error: any) => void
  ): void {
    // Try to get from dedicated endpoint first
    this.backend.get(`projects/${projectId}/statement-generations/latest`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => {
        // Fallback: Get all generations and return the first one (most recent)
        if (error?.status === 404) {
          console.log('Latest generation endpoint not found, using fallback...');
          this.getProjectGenerations(
            projectId,
            (generations: any[]) => {
              if (generations && generations.length > 0) {
                // Return the most recent one (first in array, assuming sorted by date desc)
                successCallback(generations[0]);
              } else {
                // No generations exist
                successCallback(null);
              }
            },
            errorCallback
          );
        } else {
          errorCallback(error);
        }
      }
    });
  }

  /**
   * Get details of a specific generation including statements
   */
  getGenerationDetails(
    processId: string,
    successCallback: (details: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`statements/process/${processId}/details`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Refresh generation status (for ongoing processes)
   */
  refreshGenerationStatus(
    processId: string,
    successCallback: (status: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.getProcessStatus(processId, successCallback, errorCallback);
  }

  /**
   * Get stored PDF documents for a project
   */
  getProjectPDFDocuments(
    projectId: number,
    successCallback: (pdfs: any[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/pdf-documents`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Get stored PubMed documents for a project
   */
  getProjectPubMedDocuments(
    projectId: number,
    successCallback: (documents: any[]) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.get(`projects/${projectId}/pubmed-documents`).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }

  /**
   * Save a PubMed document to a project
   */
  savePubMedDocumentToProject(
    projectId: number,
    pubmedData: any,
    successCallback: (result: any) => void,
    errorCallback: (error: any) => void
  ): void {
    this.backend.post(`projects/${projectId}/pubmed-documents`, pubmedData).subscribe({
      next: (result: any) => successCallback(result),
      error: (error) => errorCallback(error)
    });
  }
}
